import {useStorage} from '@vueuse/core';
import type {DeliveryDate} from '../interfaces/delivery.date.type';
import type {ShoppingCartOrderLine} from '../interfaces/order.line.type';
import {getUniqueDeliveryDatesForOrderLines} from './order.service';
import {isSameConsignment, isSameVarietyAndWeek} from './order.line.service';

export interface ShoppingCartStorageState {
	assortment: string;
	orderLines: ShoppingCartOrderLine[];
}

const TOGGLE_KEY = 'toggleShoppingCart';

const SHOPPING_CART_KEY: string = `shoppingcart-20181210-${window.userUniqueId}`;

export const shoppingCartToggle = useStorage(TOGGLE_KEY, false);

export const shoppingCartStorage = useStorage<ShoppingCartStorageState>(SHOPPING_CART_KEY, {
	assortment: 'NETHERLANDS',
	orderLines: []
});

const matchOrderLine = (originalOrderLine: ShoppingCartOrderLine, givenOrderLine: ShoppingCartOrderLine) => {
	return isSameVarietyAndWeek(originalOrderLine, givenOrderLine)
		&& isSameConsignment(originalOrderLine.consignment, givenOrderLine.consignment);
};

const getOrderLineIndex = (orderLine: ShoppingCartOrderLine) => {
	const index = shoppingCartStorage.value.orderLines.findIndex((line: ShoppingCartOrderLine) => matchOrderLine(line, orderLine));
	return index >= 0 ? index : null;
};

const getOrderLinesForDeliveryDate = (week: number, year: number) => {
	return shoppingCartStorage.value.orderLines.filter((orderLine: ShoppingCartOrderLine) => {
		return orderLine.week === week && orderLine.year === year;
	});
};

const replaceOrderLineInShoppingCart = (orderLine: ShoppingCartOrderLine, newOrderLine: ShoppingCartOrderLine) => {
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		return;
	}

	shoppingCartStorage.value.orderLines.splice(existingIndex, 1, newOrderLine);
};

export const updateOrderLineAmount = (orderLine: ShoppingCartOrderLine, amount: number) => {
	const newOrderLine = {...orderLine, amount};
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		return;
	}

	shoppingCartStorage.value.orderLines.splice(existingIndex, 1, newOrderLine);
};

export const updateOrderLineConsignment = (orderLine: ShoppingCartOrderLine, consignment: string | null) => {
	const newOrderLine = {...orderLine, consignment};
	replaceOrderLineInShoppingCart(orderLine, newOrderLine);
};

export const updateOrderLineConsignmentAndAmount = (orderLine: ShoppingCartOrderLine, consignment: string | null, amount: number) => {
	const newOrderLine = {...orderLine, consignment, amount: orderLine.amount + amount};
	replaceOrderLineInShoppingCart(orderLine, newOrderLine);
};

export const updateOrderLine = (orderLine: ShoppingCartOrderLine) => {
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		return;
	}

	shoppingCartStorage.value.orderLines.splice(existingIndex, 1, orderLine);
};

export const removeOrderLineFromShoppingCart = (orderLine: ShoppingCartOrderLine) => {
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		return;
	}

	shoppingCartStorage.value.orderLines.splice(existingIndex, 1);
};

export const removeOrderLinesForDeliveryDate = (deliveryDate: DeliveryDate) => {
	getOrderLinesForDeliveryDate(deliveryDate.week, deliveryDate.year).forEach((orderLine: ShoppingCartOrderLine) => {
		removeOrderLineFromShoppingCart(orderLine);
	});
};

export const removeOrderLinesForDeliveryDates = (deliveryDates: DeliveryDate[]) => {
	deliveryDates.forEach((deliveryDate: DeliveryDate) => removeOrderLinesForDeliveryDate(deliveryDate));
};

export const addOrderLineToShoppingCart = (orderLine: ShoppingCartOrderLine) => {
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		shoppingCartStorage.value.orderLines.push(orderLine);
	} else {
		// Add amount to existing order line.
		const existingOrderLine = shoppingCartStorage.value.orderLines[existingIndex];
		const newAmount = existingOrderLine.amount + orderLine.amount;
		updateOrderLineAmount(existingOrderLine, newAmount);
	}
};

export const deliveryDates = () => getUniqueDeliveryDatesForOrderLines(shoppingCartStorage.value.orderLines);
